import { useEffect, useState, useMemo } from 'react';
import { IconList, IconChevronUp } from '@tabler/icons-react';
import GithubSlugger from 'github-slugger';
import useTheme from 'chat-ui/src/hooks/useTheme';
import { cn } from '@/lib/utils';

/**
 * Interface for a table of contents item
 */
export interface TocItem {
  /** Unique ID for the heading (used for anchor links) */
  id: string;
  /** Text content of the heading */
  text: string;
  /** Heading level (2 for H2, 3 for H3) */
  level: number;
}

/**
 * Props for the TableOfContents component
 */
interface TableOfContentsProps {
  /** Markdown content to extract headings from */
  content: string;
  /** Optional CSS class name */
  className?: string;
}

/**
 * TableOfContents component
 *
 * @description
 * A floating table of contents that extracts H2 and H3 headings from markdown content
 * and provides navigation with active section highlighting.
 *
 * Features:
 * - Extracts H2 and H3 headings from markdown
 * - Smooth scroll to sections on click
 * - Highlights active section based on scroll position (Intersection Observer)
 * - Sticky/floating positioning on desktop
 * - Collapsible on narrow screens
 * - Theme-aware styling
 * - Keyboard accessible
 * - Screen reader friendly
 *
 * The component uses Intersection Observer API to track which section is currently
 * visible in the viewport and highlights the corresponding TOC item.
 *
 * @example
 * ```tsx
 * function DocumentationPage() {
 *   const { document } = useDocumentation(docPath);
 *
 *   return (
 *     <div className="flex">
 *       <main>{/* content *\/}</main>
 *       <TableOfContents content={document?.content || ''} />
 *     </div>
 *   );
 * }
 * ```
 */
export function TableOfContents({ content, className }: TableOfContentsProps) {
  const { currentTheme } = useTheme();
  const [activeId, setActiveId] = useState<string>('');
  const [isCollapsed, setIsCollapsed] = useState(false);

  /**
   * Extract headings from markdown content
   * Matches H2 (##) and H3 (###) headings
   */
  const tocItems = useMemo(() => {
    const items: TocItem[] = [];
    const slugger = new GithubSlugger();

    // Regex to match ## and ### headings
    const headingRegex = /^(#{2,3})\s+(.+)$/gm;
    let match = headingRegex.exec(content);

    while (match !== null) {
      const level = match[1].length;
      const text = match[2].trim();

      // Generate ID using github-slugger (same as rehype-slug)
      const id = slugger.slug(text);

      items.push({
        id,
        text,
        level,
      });

      match = headingRegex.exec(content);
    }

    return items;
  }, [content]);

  /**
   * Set up Intersection Observer to track active section
   */
  useEffect(() => {
    if (tocItems.length === 0) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        });
      },
      {
        rootMargin: '-100px 0px -66%',
        threshold: 0,
      },
    );

    // Observe all heading elements
    const headingElements = tocItems
      .map((item) => document.getElementById(item.id))
      .filter(Boolean) as HTMLElement[];

    headingElements.forEach((element) => observer.observe(element));

    const cleanup = () => {
      headingElements.forEach((element) => {
        observer.unobserve(element);
      });
    };

    return cleanup;
  }, [tocItems]);

  /**
   * Scroll to a section smoothly
   */
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 80; // Account for sticky header
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.scrollY - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });

      // Update URL hash without triggering scroll
      window.history.replaceState(null, '', `#${id}`);
    }
  };

  // Don't render if no headings found
  if (tocItems.length === 0) {
    return null;
  }

  return (
    <nav
      className={cn(
        'hidden lg:block',
        'sticky top-20',
        'h-fit max-h-[calc(100vh-6rem)]',
        'overflow-y-auto',
        'w-64',
        className,
      )}
      aria-label="Table of contents"
    >
      <div
        className={cn(
          'rounded-lg border p-4',
          'bg-white dark:bg-gray-950',
          'transition-all duration-200',
        )}
        style={{
          borderColor: currentTheme?.['border-color'] || '#e5e7eb',
          backgroundColor: currentTheme?.background || '#ffffff',
        }}
      >
        {/* Header */}
        <button
          type="button"
          onClick={() => setIsCollapsed(!isCollapsed)}
          className={cn(
            'flex items-center justify-between w-full mb-3 pb-3',
            'border-b',
            'text-sm font-semibold uppercase tracking-wider',
            'hover:opacity-70 transition-opacity',
          )}
          style={{
            borderColor: currentTheme?.['border-color'] || '#e5e7eb',
            color: currentTheme?.['primary-text'] || '#111827',
          }}
          aria-expanded={!isCollapsed}
          aria-controls="toc-list"
        >
          <span className="flex items-center gap-2">
            <IconList size={16} aria-hidden="true" />
            On This Page
          </span>
          <IconChevronUp
            size={16}
            className={cn('transition-transform', isCollapsed && 'rotate-180')}
            aria-hidden="true"
          />
        </button>

        {/* TOC List */}
        {!isCollapsed && (
          <ul id="toc-list" className="space-y-2">
            {tocItems.map((item) => {
              const isActive = activeId === item.id;
              const isH3 = item.level === 3;

              return (
                <li key={item.id} className={cn(isH3 && 'ml-4')}>
                  <button
                    type="button"
                    onClick={() => scrollToSection(item.id)}
                    className={cn(
                      'w-full text-left text-sm py-1 px-2 rounded',
                      'transition-all duration-150',
                      'hover:bg-gray-100 dark:hover:bg-gray-800',
                      'focus:outline-none focus:ring-2 focus:ring-offset-1',
                      isActive && 'font-semibold bg-gray-50 dark:bg-gray-900',
                    )}
                    style={{
                      color: isActive
                        ? currentTheme?.primary || '#096846'
                        : currentTheme?.['primary-text'] || '#374151',
                    }}
                    aria-current={isActive ? 'location' : undefined}
                  >
                    <span className="line-clamp-2">{item.text}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        {/* Collapsed state hint */}
        {isCollapsed && <p className="text-xs text-gray-500 text-center mt-2">Click to expand</p>}
      </div>

      {/* Mobile hint - hidden on desktop */}
      <div className="lg:hidden text-xs text-gray-500 mt-2 px-2 text-center">
        Table of contents available on desktop
      </div>
    </nav>
  );
}
